import { format, parseISO, isPast, isToday } from 'date-fns';
import type { CardStatus } from '@/types';

/** Status enum values */
export const PENDING = 0;
export const WORKING = 1;
export const DONE = 2;

/** Status options for react-admin Select */
export const CARD_STATUSES = [
    { id: PENDING, label: 'Pending' },
    { id: WORKING, label: 'Working' },
    { id: DONE, label: 'Done' },
];

/** Allowed status transitions: any status may move to any other status. */
const ALLOWED_TRANSITIONS: Record<CardStatus, Set<CardStatus>> = {
    [PENDING]: new Set([WORKING, DONE]),
    [WORKING]: new Set([PENDING, DONE]),
    [DONE]: new Set([PENDING, WORKING]),
};

/**
 * Check if a transition from one status to another is allowed.
 * A move to the same status is allowed (no-op).
 */
export function canTransition(from: CardStatus, to: CardStatus): boolean {
    if (from === to) return true;
    return ALLOWED_TRANSITIONS[from]?.has(to) ?? false;
}

/**
 * Check if a card is overdue based on its limit_date and status.
 * Cards in Done status are never overdue.
 * @param limitDate ISO datetime string or null
 * @param status Card status
 */
export function isOverdue(limitDate: string | null, status: CardStatus): boolean {
    if (status === DONE || !limitDate) return false;
    try {
        const date = parseISO(limitDate);
        // Overdue stays day-granular: a card due earlier today is not overdue.
        return isPast(date) && !isToday(date);
    } catch {
        return false;
    }
}

/**
 * Format a limit_date (now a datetime) for display (e.g. "Oct 15, 2024, 2:30 PM")
 */
export function formatLimitDate(dateString: string | null): string {
    if (!dateString) return '';
    try {
        const date = parseISO(dateString);
        return format(date, 'MMM d, yyyy, h:mm a');
    } catch {
        return dateString;
    }
}

/**
 * Convert an ISO datetime into the local "YYYY-MM-DDTHH:mm" an <input type="datetime-local">
 * expects. Empty string for null/unparseable.
 */
export function toDateTimeLocalInput(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
        return format(parseISO(dateString), "yyyy-MM-dd'T'HH:mm");
    } catch {
        return '';
    }
}

/**
 * Get the label for a status code
 */
export function getStatusLabel(status: CardStatus): string {
    const option = CARD_STATUSES.find(s => s.id === status);
    return option?.label ?? 'Unknown';
}
